import { Alert, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { ForgetBotModal } from "./ForgetBotModal";
import type { RepoHostTokenSummary } from "./tokensApi";

export interface BotAccountsSectionProps {
  readonly tokens: readonly RepoHostTokenSummary[];
  readonly onChanged: () => void;
}

// Bot-аккаунты выводим из списка токенов: один host_username = один
// сервисный аккаунт (несколько per-role токенов могут принадлежать
// одному боту). Уникализируем сохраняя порядок появления.
function uniqueBotUsernames(
  tokens: readonly RepoHostTokenSummary[],
): readonly string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const token of tokens) {
    if (!seen.has(token.host_username)) {
      seen.add(token.host_username);
      result.push(token.host_username);
    }
  }
  return result;
}

// Секция «Сервисные аккаунты» на `/settings/connections`. Показывает
// уникальные bot-аккаунты org (по host_username токенов) и действие
// «Забыть бота» (модалка — в ForgetBotModal.tsx: гейт подтверждения логином + выбор
// действия с учёткой провайдера). Эталон destructive-действия —
// disconnect-флоу ForgejoSection.
export function BotAccountsSection(
  props: BotAccountsSectionProps,
): ReactElement {
  const { t } = useTranslation();
  const { tokens, onChanged } = props;

  const [forgetUsername, setForgetUsername] = useState<string | null>(null);
  // Persistent success-подтверждение забывания — на уровне секции, как
  // disconnectInfo в ForgejoSection. Очищается при открытии новой модалки.
  const [forgottenUsername, setForgottenUsername] = useState<string | null>(
    null,
  );

  const botUsernames = uniqueBotUsernames(tokens);

  return (
    <>
      <Stack gap="md">
        <Title order={3}>{t("connections.tokens.botAccounts.title")}</Title>
        <Text c="dimmed" size="sm">
          {t("connections.tokens.botAccounts.description")}
        </Text>

        {forgottenUsername !== null ? (
          <Alert color="green">
            {t("connections.tokens.botAccounts.success", {
              username: forgottenUsername,
            })}
          </Alert>
        ) : null}

        {botUsernames.length === 0 ? null : (
          <Stack gap="sm">
            {botUsernames.map((username) => (
              <Card key={username} withBorder p="md">
                <Group justify="space-between" align="center">
                  <Text size="sm">
                    <strong>
                      {t("connections.tokens.botAccounts.owner")}:
                    </strong>{" "}
                    {username}
                  </Text>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    onClick={() => {
                      setForgottenUsername(null);
                      setForgetUsername(username);
                    }}
                  >
                    {t("connections.tokens.botAccounts.forgetButton")}
                  </Button>
                </Group>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Confirm-модалка забывания — рендерим только при выбранном боте,
          чтобы тело (confirm-input + provider_action) пересоздавалось. */}
      {forgetUsername !== null ? (
        <ForgetBotModal
          username={forgetUsername}
          onClose={() => {
            setForgetUsername(null);
          }}
          onChanged={onChanged}
          onForgotten={setForgottenUsername}
        />
      ) : null}
    </>
  );
}
